import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wallet, Percent, ShoppingBag, DollarSign, LogOut, Copy, Check,
  CreditCard, KeyRound, Search, ExternalLink, ShieldCheck, History,
  LayoutDashboard, Store, Settings as SettingsIcon, Loader2, Bitcoin,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Input } from "@/components/ui/input";
import { api, apiError, aud } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const cents = (c) => aud((c || 0) / 100);
const TYPE_META = {
  deposit: { label: "Stripe deposit", cls: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30" },
  purchase: { label: "Purchase", cls: "text-rose-300 bg-rose-400/10 border-rose-400/30" },
  refund: { label: "Refund", cls: "text-amber-300 bg-amber-400/10 border-amber-400/30" },
  chargeback: { label: "Chargeback", cls: "text-red-300 bg-red-400/10 border-red-400/30" },
  admin_credit: { label: "Admin credit", cls: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30" },
  admin_debit: { label: "Admin debit", cls: "text-rose-300 bg-rose-400/10 border-rose-400/30" },
  admin_set: { label: "Admin set", cls: "text-sky-300 bg-sky-400/10 border-sky-400/30" },
};

function CopyText({ text, testid }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); } catch {}
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#1E2D4A] text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-white hover:border-[#2E6BFF]/50 transition-all"
    >
      {ok ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {ok ? "Copied" : "Copy"}
    </button>
  );
}

export default function ResellerDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const token = localStorage.getItem("desync_reseller_token");
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [catalog, setCatalog] = useState(null);
  const [orders, setOrders] = useState(null);
  const [txns, setTxns] = useState(null);
  const [deposits, setDeposits] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [buying, setBuying] = useState(null); // {product, duration, qty}
  const [buyQty, setBuyQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [lastKeys, setLastKeys] = useState(null);
  const [depAmount, setDepAmount] = useState("");
  const [depMethod, setDepMethod] = useState("stripe");
  const [cryptoCoins, setCryptoCoins] = useState([]);
  const [cryptoCoin, setCryptoCoin] = useState("");
  const [activeCrypto, setActiveCrypto] = useState(null); // status object of the in-flight crypto deposit
  const [pwForm, setPwForm] = useState({ current: "", next: "" });

  const logout = useCallback(() => {
    localStorage.removeItem("desync_reseller_token");
    navigate("/reseller/login");
  }, [navigate]);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/reseller/me", auth);
      setMe(data);
      return data;
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error(apiError(e));
        logout();
      }
      return null;
    }
  }, [auth, logout]);

  useEffect(() => {
    if (!token) { navigate("/reseller/login"); return; }
    loadMe();
  }, [token, navigate, loadMe]);

  useEffect(() => {
    const dep = params.get("deposit");
    if (dep === "success") toast.success("Deposit received — credit added to your wallet");
    if (dep === "cancelled") toast.error("Deposit cancelled");
    if (dep) { params.delete("deposit"); setParams(params, { replace: true }); loadMe(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return;
    if (tab === "store" && !catalog) api.get("/reseller/products", auth).then(({ data }) => setCatalog(data)).catch((e) => toast.error(apiError(e)));
    if (tab === "orders" && !orders) api.get("/reseller/orders", auth).then(({ data }) => setOrders(data)).catch(() => {});
    if ((tab === "transactions" || tab === "overview") && !txns) api.get("/reseller/transactions", auth).then(({ data }) => setTxns(data)).catch(() => {});
    if (tab === "credit" && !deposits) api.get("/reseller/deposits", auth).then(({ data }) => setDeposits(data)).catch(() => {});
    if (tab === "credit" && cryptoCoins.length === 0) {
      api.get("/payments/crypto/coins").then(({ data }) => {
        const coins = data.coins || [];
        setCryptoCoins(coins);
        setCryptoCoin((c) => c || (coins[0]?.id ?? ""));
      }).catch(() => {});
    }
  }, [tab, token, auth, catalog, orders, txns, deposits, cryptoCoins.length]);

  // poll an in-flight crypto deposit until it credits
  useEffect(() => {
    if (!activeCrypto || activeCrypto.status !== "pending") return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/reseller/deposits/crypto/status/${activeCrypto.deposit_id}`, auth);
        if (data.status === "completed") {
          toast.success("Crypto deposit confirmed — credit added");
          setActiveCrypto(null);
          refreshAll();
        } else {
          setActiveCrypto(data);
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrypto?.deposit_id, activeCrypto?.status]);

  const refreshAll = async () => {
    setTxns(null); setOrders(null); setDeposits(null);
    await loadMe();
    api.get("/reseller/transactions", auth).then(({ data }) => setTxns(data));
    api.get("/reseller/orders", auth).then(({ data }) => setOrders(data));
    api.get("/reseller/deposits", auth).then(({ data }) => setDeposits(data));
    api.get("/reseller/products", auth).then(({ data }) => setCatalog(data));
  };

  const buy = async () => {
    if (!buying) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reseller/purchase", {
        items: [{ product_id: buying.product.id, duration: buying.duration, qty: buyQty }],
      }, auth);
      toast.success("Purchase complete — keys delivered");
      const keys = [];
      for (const it of data.order.items) {
        for (const d of it.deliverables || []) keys.push(d.license_key || (d.account && d.account.raw) || "—");
      }
      setLastKeys({ order: data.order, keys });
      setBuying(null);
      await refreshAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deposit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reseller/deposits/stripe", {
        amount: parseFloat(depAmount), origin_url: window.location.origin,
      }, auth);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const depositCrypto = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reseller/deposits/crypto", {
        amount: parseFloat(depAmount), coin: cryptoCoin,
      }, auth);
      const st = await api.get(`/reseller/deposits/crypto/status/${data.deposit_id}`, auth);
      setActiveCrypto(st.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const changePw = async () => {
    setBusy(true);
    try {
      await api.post("/reseller/change-password", {
        current_password: pwForm.current, new_password: pwForm.next,
      }, auth);
      toast.success("Password updated");
      setPwForm({ current: "", next: "" });
      await loadMe();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!me) {
    return (
      <div className="min-h-screen bg-[#050B18] flex items-center justify-center text-slate-400 gap-3" data-testid="reseller-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading your portal…
      </div>
    );
  }

  const categories = catalog ? ["all", ...new Set(catalog.products.map((p) => p.game))] : ["all"];
  const shown = (catalog?.products || []).filter((p) =>
    (catFilter === "all" || p.game === catFilter) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  const TABS = [
    ["overview", "Overview", LayoutDashboard],
    ["store", "Store", Store],
    ["credit", "Add Credit", CreditCard],
    ["orders", "My Orders", ShoppingBag],
    ["transactions", "Transactions", History],
    ["settings", "Settings", SettingsIcon],
  ];

  return (
    <div className="min-h-screen bg-[#050B18] text-slate-100" data-testid="reseller-dashboard">
      {/* force password change */}
      {me.must_change_password && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="force-pw-modal">
          <div className="w-full max-w-sm p-8 rounded-2xl bg-[#0A1628] border border-amber-500/40">
            <ShieldCheck className="w-8 h-8 text-amber-400 mb-3" />
            <h2 className="font-display text-xl font-bold text-white mb-1">Set your own password</h2>
            <p className="text-sm text-slate-400 mb-5">Your account uses a temporary password. Choose a new one to continue.</p>
            <div className="space-y-3">
              <Input type="password" placeholder="Temporary password" value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                data-testid="force-pw-current" className="bg-[#050B18] border-[#1E2D4A] h-11" />
              <Input type="password" placeholder="New password (8+ characters)" value={pwForm.next}
                onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                data-testid="force-pw-new" className="bg-[#050B18] border-[#1E2D4A] h-11" />
              <button onClick={changePw} disabled={busy || !pwForm.current || pwForm.next.length < 8}
                data-testid="force-pw-submit"
                className="w-full px-6 py-3 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold disabled:opacity-40 transition-all">
                {busy ? "Saving..." : "Set password & continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* header */}
      <div className="border-b border-[#1E2D4A] bg-[#0A1628]/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/images/logo.svg" alt="" className="w-8 h-8" />
            <span className="font-display font-extrabold tracking-tight">De<span className="text-[#2E6BFF]">sync</span></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-[#1E2D4A] rounded px-2 py-0.5 ml-1">Reseller</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Balance</div>
              <div className="font-mono font-bold text-emerald-300" data-testid="header-balance">{cents(me.balance_cents)}</div>
            </div>
            <span className="text-xs text-slate-500 hidden md:inline">{me.email}</span>
            <button onClick={logout} data-testid="reseller-logout"
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-white transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* tabs */}
        <div className="flex gap-1.5 mb-8 overflow-x-auto pb-1" data-testid="reseller-tabs">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`reseller-tab-${id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-mono font-bold uppercase tracking-widest whitespace-nowrap transition-all ${
                tab === id ? "bg-[#2E6BFF] text-white" : "text-slate-500 hover:text-white border border-[#1E2D4A]"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* overview */}
        {tab === "overview" && (
          <div data-testid="reseller-overview">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
              {[
                { icon: Wallet, label: "Available Credit", value: cents(me.balance_cents), testid: "ov-credit", accent: "text-emerald-300" },
                { icon: Percent, label: "Your Discount", value: `${me.discount_percent}%`, testid: "ov-discount", accent: "text-[#7FB0FF]" },
                { icon: ShoppingBag, label: "Total Orders", value: me.total_orders, testid: "ov-orders" },
                { icon: DollarSign, label: "Total Spent", value: aud(me.total_spent), testid: "ov-spent" },
                { icon: CreditCard, label: "Credit Deposited", value: aud(me.total_deposited), testid: "ov-deposited" },
              ].map(({ icon: Icon, label, value, testid, accent }) => (
                <motion.div key={label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-[#0A1628] border border-[#1E2D4A]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</span>
                    <Icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className={`font-mono text-xl font-bold ${accent || "text-white"}`} data-testid={testid}>{value}</div>
                </motion.div>
              ))}
            </div>
            <h3 className="font-display font-bold text-lg mb-3">Recent activity</h3>
            <TxnTable txns={(txns || []).slice(0, 8)} />
          </div>
        )}

        {/* store */}
        {tab === "store" && (
          <div data-testid="reseller-store">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
                  data-testid="reseller-search" className="pl-9 bg-[#0A1628] border-[#1E2D4A]" />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {categories.map((c) => (
                  <button key={c} onClick={() => setCatFilter(c)} data-testid={`reseller-cat-${c.toLowerCase().replace(/\W+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-mono font-bold uppercase tracking-widest transition-all ${
                      catFilter === c ? "bg-[#2E6BFF] text-white" : "border border-[#1E2D4A] text-slate-500 hover:text-white"
                    }`}>{c}</button>
                ))}
              </div>
            </div>
            {!catalog ? (
              <div className="text-slate-500 text-sm py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading catalogue…</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {shown.map((p) => (
                  <div key={p.id} className="p-5 rounded-xl bg-[#0A1628] border border-[#1E2D4A] hover:border-[#2E6BFF]/40 transition-colors" data-testid={`reseller-product-${p.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-semibold text-white text-sm">{p.name}</div>
                      {p.reseller_excluded && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5 shrink-0">No discount</span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-4">{p.game}</div>
                    <div className="space-y-1.5">
                      {Object.entries(p.prices).map(([dur, pr]) => {
                        const stk = p.delivery === "ticket" ? 999 : (p.stock?.[dur] || 0);
                        const out = p.delivery !== "ticket" && stk === 0;
                        return (
                          <button key={dur} disabled={out}
                            onClick={() => { setBuying({ product: p, duration: dur }); setBuyQty(1); }}
                            data-testid={`reseller-buy-${p.id}-${dur}`}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-[#1E2D4A] hover:border-[#2E6BFF]/60 hover:bg-[#2E6BFF]/5 disabled:opacity-40 disabled:pointer-events-none transition-all group">
                            <span className="text-xs text-slate-400 group-hover:text-slate-200">
                              {dur} {p.delivery !== "ticket" && <span className="text-slate-600">· {stk} left</span>}
                              {out && <span className="text-rose-400 ml-1">sold out</span>}
                            </span>
                            <span className="font-mono text-xs">
                              <span className="text-slate-600 line-through mr-2">{cents(pr.retail_cents)}</span>
                              <span className="text-emerald-300 font-bold">{cents(pr.reseller_cents)}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {shown.length === 0 && <div className="col-span-full text-center py-16 text-slate-500 font-mono text-sm">No products match</div>}
              </div>
            )}
          </div>
        )}

        {/* add credit */}
        {tab === "credit" && (
          <div className="max-w-lg" data-testid="reseller-credit">
            <div className="p-6 rounded-xl bg-[#0A1628] border border-[#1E2D4A] mb-6">
              <h3 className="font-display font-bold text-lg mb-1">Add credit</h3>
              <p className="text-sm text-slate-500 mb-4">
                Minimum deposit {aud(me.min_deposit)}. Credit lands the moment your payment confirms.
              </p>
              <div className="flex gap-2 mb-4" data-testid="deposit-method-toggle">
                <button onClick={() => { setDepMethod("stripe"); }} data-testid="deposit-method-stripe"
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-mono font-bold uppercase tracking-widest transition-all ${
                    depMethod === "stripe" ? "border-[#2E6BFF] bg-[#2E6BFF]/15 text-[#7FB0FF]" : "border-[#1E2D4A] text-slate-500 hover:text-white"
                  }`}>
                  <CreditCard className="w-3.5 h-3.5" /> Card (Stripe)
                </button>
                {cryptoCoins.length > 0 && (
                  <button onClick={() => setDepMethod("crypto")} data-testid="deposit-method-crypto"
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-mono font-bold uppercase tracking-widest transition-all ${
                      depMethod === "crypto" ? "border-[#F7931A] bg-[#F7931A]/15 text-[#F5B45E]" : "border-[#1E2D4A] text-slate-500 hover:text-white"
                    }`}>
                    <Bitcoin className="w-3.5 h-3.5" /> Crypto
                  </button>
                )}
              </div>

              {depMethod === "stripe" && (
                <div className="flex gap-2">
                  <Input type="number" min={me.min_deposit} step="0.01" placeholder={`Amount (AUD, min ${me.min_deposit})`}
                    value={depAmount} onChange={(e) => setDepAmount(e.target.value)}
                    data-testid="deposit-amount-input" className="bg-[#050B18] border-[#1E2D4A] h-11 font-mono" />
                  <button onClick={deposit} disabled={busy || !depAmount}
                    data-testid="deposit-submit"
                    className="shrink-0 inline-flex items-center gap-2 px-5 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold disabled:opacity-40 transition-all">
                    <ExternalLink className="w-4 h-4" /> {busy ? "Opening…" : "Pay with Stripe"}
                  </button>
                </div>
              )}

              {depMethod === "crypto" && !activeCrypto && (
                <>
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="deposit-coin-picker">
                    {cryptoCoins.map((c) => {
                      const dup = cryptoCoins.filter((x) => x.symbol === c.symbol).length > 1;
                      return (
                        <button key={c.id} onClick={() => setCryptoCoin(c.id)} data-testid={`deposit-coin-${c.id}`}
                          title={`${c.name} — ${c.network}`}
                          className={`px-3 py-2 rounded-lg border text-[11px] font-mono font-bold uppercase tracking-widest transition-all ${
                            cryptoCoin === c.id ? "border-[#F7931A] bg-[#F7931A]/15 text-[#F5B45E]" : "border-[#1E2D4A] text-slate-500 hover:border-[#F7931A]/40"
                          }`}>
                          {dup ? `${c.symbol}·${c.short_net}` : c.symbol}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    <Input type="number" min={me.min_deposit} step="0.01" placeholder={`Amount (AUD, min ${me.min_deposit})`}
                      value={depAmount} onChange={(e) => setDepAmount(e.target.value)}
                      data-testid="deposit-crypto-amount-input" className="bg-[#050B18] border-[#1E2D4A] h-11 font-mono" />
                    <button onClick={depositCrypto} disabled={busy || !depAmount || !cryptoCoin}
                      data-testid="deposit-crypto-submit"
                      className="shrink-0 inline-flex items-center gap-2 px-5 rounded-lg border border-[#F7931A]/50 text-[#F5B45E] hover:bg-[#F7931A]/10 text-sm font-semibold disabled:opacity-40 transition-all">
                      {busy ? "Creating…" : "Get deposit address"}
                    </button>
                  </div>
                </>
              )}

              {depMethod === "crypto" && activeCrypto && (
                <div className="mt-2" data-testid="deposit-crypto-panel">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono font-bold uppercase tracking-widest mb-4" data-testid="deposit-crypto-status">
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    {activeCrypto.txid ? `Confirming (${Math.min(activeCrypto.confirmations, activeCrypto.confirmations_required)}/${activeCrypto.confirmations_required})` : "Waiting for payment — updates automatically"}
                  </div>
                  <div className="flex justify-center mb-4">
                    <div className="p-2.5 bg-white rounded-lg">
                      <QRCodeSVG value={activeCrypto.payment_uri || activeCrypto.address} size={160} level="M" />
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#050B18] border border-[#1E2D4A]">
                      <div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Send exactly ({activeCrypto.network})</div>
                        <span className="font-mono text-base font-bold text-white" data-testid="deposit-crypto-amount">
                          {activeCrypto.expected_amount} {activeCrypto.symbol}
                        </span>
                      </div>
                      <CopyText text={String(activeCrypto.expected_amount)} testid="deposit-copy-amount" />
                    </div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#050B18] border border-[#1E2D4A]">
                      <span className="font-mono text-[11px] text-slate-300 break-all" data-testid="deposit-crypto-address">{activeCrypto.address}</span>
                      <CopyText text={activeCrypto.address} testid="deposit-copy-address" />
                    </div>
                    {activeCrypto.underpaid && (
                      <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200" data-testid="deposit-crypto-underpaid">
                        Received {activeCrypto.paid_amount} {activeCrypto.symbol} — short of {activeCrypto.expected_amount}. Send the remainder to the same address.
                      </div>
                    )}
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Only send {activeCrypto.symbol} on the {activeCrypto.network} network. ≈ {cents(activeCrypto.amount_cents)} credit once confirmed.
                    </p>
                    <button onClick={() => setActiveCrypto(null)} data-testid="deposit-crypto-dismiss"
                      className="text-xs text-slate-500 hover:text-white transition-colors">
                      Hide (deposit continues in the background)
                    </button>
                  </div>
                </div>
              )}
            </div>
            <h3 className="font-display font-bold text-lg mb-3">Deposit history</h3>
            <div className="space-y-2">
              {(deposits || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0A1628] border border-[#1E2D4A]" data-testid={`deposit-row-${d.id}`}>
                  <span className="text-xs text-slate-400 font-mono">{new Date(d.created_at).toLocaleString()}</span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    {d.provider === "crypto" ? `${(d.crypto?.coin || "").toUpperCase()} · crypto` : "Stripe"}
                  </span>
                  <span className="font-mono text-sm text-white">{cents(d.amount_cents)}</span>
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                    d.status === "completed" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"
                    : d.status === "pending" ? "text-amber-300 border-amber-400/30 bg-amber-400/10"
                    : "text-rose-300 border-rose-400/30 bg-rose-400/10"}`} data-testid={`deposit-status-${d.id}`}>{d.status}</span>
                </div>
              ))}
              {deposits && deposits.length === 0 && <div className="text-slate-600 text-sm font-mono py-8 text-center">No deposits yet</div>}
            </div>
          </div>
        )}

        {/* orders */}
        {tab === "orders" && (
          <div className="space-y-4" data-testid="reseller-orders">
            {(orders || []).map((o) => (
              <div key={o.id} className="p-5 rounded-xl bg-[#0A1628] border border-[#1E2D4A]" data-testid={`reseller-order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <span className="font-mono text-xs text-slate-500">#{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleString()}</span>
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                    o.payment_status === "paid" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" : "text-amber-300 border-amber-400/30 bg-amber-400/10"}`}>
                    {o.payment_status}
                  </span>
                </div>
                {o.items.map((it, i) => (
                  <div key={i} className="border-t border-[#1E2D4A] pt-3 mt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white">{it.name} — {it.duration_label}{it.qty > 1 ? ` × ${it.qty}` : ""}</span>
                      <span className="font-mono text-sm text-emerald-300">{aud(it.unit_price * (it.qty || 1))}</span>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {(it.deliverables || []).map((d, j) => {
                        const txt = d.license_key || (d.account && d.account.raw) || "";
                        return txt ? (
                          <div key={j} className="flex items-center justify-between gap-3 p-2 rounded bg-[#050B18] border border-[#1E2D4A]">
                            <span className="font-mono text-xs text-slate-300 break-all">{txt}</span>
                            <CopyText text={txt} testid={`copy-key-${o.id}-${i}-${j}`} />
                          </div>
                        ) : null;
                      })}
                      {it.key_pending && <div className="text-xs text-amber-400 font-mono">Key being restocked — we'll email it to you</div>}
                      {it.ticket_url && <a href={it.ticket_url} target="_blank" rel="noreferrer" className="text-xs text-[#7FB0FF] underline underline-offset-2">Delivered via Discord ticket</a>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {orders && orders.length === 0 && <div className="text-slate-600 text-sm font-mono py-16 text-center">No orders yet — grab something from the store tab</div>}
          </div>
        )}

        {/* transactions */}
        {tab === "transactions" && (
          <div data-testid="reseller-transactions">
            <TxnTable txns={txns || []} full />
          </div>
        )}

        {/* settings */}
        {tab === "settings" && (
          <div className="max-w-md space-y-6" data-testid="reseller-settings">
            <div className="p-6 rounded-xl bg-[#0A1628] border border-[#1E2D4A]">
              <h3 className="font-display font-bold text-lg mb-4">Change password</h3>
              <div className="space-y-3">
                <Input type="password" placeholder="Current password" value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                  data-testid="settings-pw-current" className="bg-[#050B18] border-[#1E2D4A] h-11" />
                <Input type="password" placeholder="New password (8+ characters)" value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                  data-testid="settings-pw-new" className="bg-[#050B18] border-[#1E2D4A] h-11" />
                <button onClick={changePw} disabled={busy || !pwForm.current || pwForm.next.length < 8}
                  data-testid="settings-pw-submit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold disabled:opacity-40 transition-all">
                  <KeyRound className="w-4 h-4" /> {busy ? "Saving..." : "Update password"}
                </button>
              </div>
            </div>
            <div className="p-6 rounded-xl bg-[#0A1628] border border-[#1E2D4A] text-sm text-slate-400 space-y-1.5">
              <div>Account: <span className="text-white">{me.email}</span></div>
              <div>Reseller since: <span className="text-white">{me.created_at ? new Date(me.created_at).toLocaleDateString() : "—"}</span></div>
              <div>Discount: <span className="text-emerald-300 font-mono">{me.discount_percent}%</span> <span className="text-slate-600">(set by Desync — contact us to change it)</span></div>
            </div>
          </div>
        )}
      </div>

      {/* buy confirm modal */}
      {buying && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="buy-modal">
          <div className="w-full max-w-sm p-6 rounded-2xl bg-[#0A1628] border border-[#1E2D4A]">
            <h3 className="font-display font-bold text-lg text-white mb-1">{buying.product.name}</h3>
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">{buying.duration}</p>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-400">Quantity</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setBuyQty(Math.max(1, buyQty - 1))} data-testid="buy-qty-minus" className="w-8 h-8 rounded border border-[#1E2D4A] text-slate-400 hover:text-white">−</button>
                <span className="font-mono w-8 text-center" data-testid="buy-qty">{buyQty}</span>
                <button onClick={() => setBuyQty(Math.min(25, buyQty + 1))} data-testid="buy-qty-plus" className="w-8 h-8 rounded border border-[#1E2D4A] text-slate-400 hover:text-white">+</button>
              </div>
            </div>
            <div className="flex items-center justify-between mb-1 text-sm">
              <span className="text-slate-400">Retail</span>
              <span className="font-mono text-slate-500 line-through">{cents(buying.product.prices[buying.duration].retail_cents * buyQty)}</span>
            </div>
            <div className="flex items-center justify-between mb-5">
              <span className="text-slate-300 font-semibold">You pay</span>
              <span className="font-mono text-lg font-bold text-emerald-300" data-testid="buy-total">{cents(buying.product.prices[buying.duration].reseller_cents * buyQty)}</span>
            </div>
            <div className="flex items-center justify-between mb-5 text-xs">
              <span className="text-slate-500">Balance after</span>
              <span className={`font-mono ${me.balance_cents - buying.product.prices[buying.duration].reseller_cents * buyQty < 0 ? "text-rose-400" : "text-slate-300"}`} data-testid="buy-balance-after">
                {cents(me.balance_cents - buying.product.prices[buying.duration].reseller_cents * buyQty)}
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setBuying(null)} data-testid="buy-cancel"
                className="flex-1 px-4 py-2.5 rounded-lg border border-[#1E2D4A] text-slate-400 text-sm hover:text-white transition-colors">Cancel</button>
              <button onClick={buy} disabled={busy}
                data-testid="buy-confirm"
                className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#050B18] text-sm font-bold disabled:opacity-40 transition-all">
                {busy ? "Processing…" : "Buy with credit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delivered keys modal */}
      {lastKeys && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="keys-modal">
          <div className="w-full max-w-md p-6 rounded-2xl bg-[#0A1628] border border-emerald-500/40">
            <h3 className="font-display font-bold text-lg text-white mb-1">Delivered instantly</h3>
            <p className="text-sm text-slate-400 mb-4">Order #{lastKeys.order.id.slice(0, 8).toUpperCase()} — also emailed to you and saved under My Orders.</p>
            <div className="space-y-2 mb-5 max-h-64 overflow-y-auto">
              {lastKeys.keys.map((k, i) => (
                <div key={i} className="flex items-center justify-between gap-3 p-2.5 rounded bg-[#050B18] border border-[#1E2D4A]">
                  <span className="font-mono text-xs text-slate-200 break-all">{k}</span>
                  <CopyText text={k} testid={`delivered-copy-${i}`} />
                </div>
              ))}
            </div>
            <button onClick={() => { setLastKeys(null); setTab("orders"); }} data-testid="keys-done"
              className="w-full px-4 py-2.5 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold transition-all">
              View my orders
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TxnTable({ txns, full = false }) {
  return (
    <div className="rounded-xl border border-[#1E2D4A] overflow-hidden">
      <table className="w-full text-sm" data-testid={full ? "txn-table" : "txn-table-preview"}>
        <thead>
          <tr className="bg-[#0A1628] text-[10px] font-mono uppercase tracking-widest text-slate-500">
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5">Transaction</th>
            <th className="text-right px-4 py-2.5">Amount</th>
            <th className="text-right px-4 py-2.5">Balance</th>
          </tr>
        </thead>
        <tbody>
          {txns.map((t) => {
            const meta = TYPE_META[t.type] || { label: t.type, cls: "text-slate-300 bg-slate-400/10 border-slate-400/30" };
            return (
              <tr key={t.id} className="border-t border-[#1E2D4A] bg-[#0A1628]/50" data-testid={`txn-row-${t.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                  {t.note && <span className="ml-2 text-xs text-slate-500">{t.note}</span>}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono text-xs font-bold ${t.amount_cents >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {t.amount_cents >= 0 ? "+" : ""}{cents(t.amount_cents)}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-300">{cents(t.balance_after_cents)}</td>
              </tr>
            );
          })}
          {txns.length === 0 && (
            <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-600 font-mono text-xs">No transactions yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
